import { INTENT_TYPE } from '../../constants';
import { stripLeadingPhrase } from '../features/cues';
import { hasOperandEvidence } from '../features/operand-evidence';
import { NAVIGATION_CUES, POLITENESS_PREFIXES } from '../pack/lexicons';
import { type ConvergenceRule, type FeatureContext } from '../types';

/**
 * `S-NAVIGATE` (priority 970) — an explicit request to go somewhere stays on Search.
 *
 * The last Search protection. "Take me to the launch plan" is navigation, and the
 * entry experience already does navigation well; routing it to Chat would replace a
 * click with a conversation.
 *
 * The cue set splits in two on purpose:
 *
 * - `go to`, `navigate to`, `take me to`, `visit` are unambiguous, so operand
 *   evidence is enough.
 * - `open` is also an ordinary English word, so it needs `navigationTarget=true` —
 *   a complete protected atom, or a singular navigable artefact plus further
 *   evidence naming which one. Bare `open` does not win merely because text
 *   follows; that case belongs to `S-AMBIGUOUS-OPEN`.
 */
export const matchesNavigate = (features: FeatureContext): boolean => {
	const qMatch = features.qMatch;
	const politeness = stripLeadingPhrase(qMatch, POLITENESS_PREFIXES);
	const cue = stripLeadingPhrase(politeness ? politeness.rest : qMatch, NAVIGATION_CUES);

	if (cue) {
		return hasOperandEvidence(cue.rest);
	}

	// `open` is resolved through the shared navigation-target feature so this rule and
	// `S-AMBIGUOUS-OPEN` can never disagree about what counts as a target. The feature is
	// false unless an `open` cue leads the query, so no separate cue check is needed here.
	return features.navigationTarget;
};

export const navigateRule: ConvergenceRule = {
	type: INTENT_TYPE.FPS,
	pattern: 'NAVIGATE',
	matches: matchesNavigate,
};
